import { Button } from '@shared/components/ui/button';
import { DatePicker, ExpiryDatePicker } from '@shared/components/ui/date-picker';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { useToast } from '@shared/hooks/use-toast';
import { useCreateBatch } from '@modules/Batches/hooks/useBatchMutations';
import { format } from 'date-fns';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProductInfo {
  id: number;
  name: string;
  code: string;
}

export interface ReceiveCreateBatchModalRef {
  open: (product: ProductInfo, onCreated: (batchId: number) => void) => void;
}

export const ReceiveCreateBatchModal = forwardRef<ReceiveCreateBatchModalRef>((_, ref) => {
  const { t } = useTranslation('merchandiseV3');
  const { t: tBatches } = useTranslation('batchesModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const createBatch = useCreateBatch();
  const onCreatedRef = useRef<((batchId: number) => void) | null>(null);

  const [product, setProduct] = useState<ProductInfo | null>(null);
  const [batchNumber, setBatchNumber] = useState('');
  const [receivedAtDate, setReceivedAtDate] = useState<Date | undefined>(new Date());
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined);
  const [supplierReference, setSupplierReference] = useState('');

  const resetForm = () => {
    setProduct(null);
    setBatchNumber('');
    setReceivedAtDate(new Date());
    setExpiryDate(undefined);
    setSupplierReference('');
    onCreatedRef.current = null;
  };

  useImperativeHandle(ref, () => ({
    open: (productInfo: ProductInfo, onCreated: (batchId: number) => void) => {
      setProduct(productInfo);
      setBatchNumber('');
      setReceivedAtDate(new Date());
      setExpiryDate(undefined);
      setSupplierReference('');
      onCreatedRef.current = onCreated;
      modalRef.current?.openModal();
    },
  }));

  const handleCancel = () => {
    modalRef.current?.closeModal();
    resetForm();
  };

  const handleSubmit = async () => {
    if (!product || !batchNumber.trim() || !receivedAtDate) {
      toast({
        title: t('receive.create_batch.error_title', 'Datos incompletos'),
        description: t('receive.create_batch.error_description', 'Indica el número de lote y la fecha de recepción.'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await createBatch.mutateAsync({
        product_id: product.id,
        batch_number: batchNumber.trim(),
        received_at: format(receivedAtDate, 'yyyy-MM-dd'),
        expiry_date: expiryDate ? format(expiryDate, 'yyyy-MM-dd') : null,
        supplier_reference: supplierReference.trim() || null,
      });

      toast({
        title: t('receive.create_batch.success_title', 'Lote registrado'),
        description: t('receive.create_batch.success_description', 'El lote se creó correctamente.'),
      });

      onCreatedRef.current?.(result.id);
      modalRef.current?.closeModal();
      resetForm();
    } catch (error: any) {
      toast({
        title: t('receive.create_batch.error_title', 'Error al registrar lote'),
        description: error?.response?.data?.message || error?.message || tBatches('toast.createError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('receive.create_batch.title', 'Registrar lote')}
      type="info"
      width="md"
      onCloseModal={resetForm}
    >
      <div className="space-y-4">
        {product && (
          <Product
            id={product.id}
            name={product.name}
            code={product.code}
            src=""
            showImageModal={false}
            enableLineClamp={true}
          />
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{tBatches('modals.create.batch_number')} *</label>
          <Input
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
            placeholder={tBatches('modals.create.batch_number_placeholder')}
            data-testid="receive-create-batch-number"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{tBatches('modals.create.received_at')} *</label>
          <DatePicker
            date={receivedAtDate}
            onDateChange={setReceivedAtDate}
            placeholder={tBatches('modals.create.date_placeholder')}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{tBatches('modals.create.expiry_date')}</label>
          <ExpiryDatePicker
            date={expiryDate}
            onDateChange={setExpiryDate}
            fromYear={new Date().getFullYear()}
            toYear={new Date().getFullYear() + 20}
            labels={{
              day: t('receive.create_batch.expiry_day', 'Día'),
              month: t('receive.create_batch.expiry_month', 'Mes'),
              year: t('receive.create_batch.expiry_year', 'Año'),
              preview: t('receive.create_batch.expiry_preview', 'Fecha seleccionada'),
              search: t('receive.create_batch.expiry_search', 'Buscar...'),
              notFound: t('receive.create_batch.expiry_not_found', 'Sin resultados'),
            }}
            data-testid="receive-create-batch-expiry"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{tBatches('modals.create.supplier_reference')}</label>
          <Input
            value={supplierReference}
            onChange={(e) => setSupplierReference(e.target.value)}
            placeholder={tBatches('modals.create.supplier_reference_placeholder')}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={handleCancel} disabled={createBatch.isPending}>
            {tBatches('modals.create.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createBatch.isPending || !batchNumber.trim() || !receivedAtDate}
            data-testid="receive-create-batch-submit"
          >
            {createBatch.isPending
              ? t('receive.create_batch.saving', 'Registrando...')
              : t('receive.create_batch.submit', 'Registrar lote')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ReceiveCreateBatchModal.displayName = 'ReceiveCreateBatchModal';
